import { useEffect, useState } from "react";
import { Loader2, PenLine, Music } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const CHIPS = ["More energy", "Slower tempo", "Different vocalist", "Punchier mix", "More instruments"];

export const RevisionDialog = ({ open, onOpenChange, siteKey, track, trackLabel, roundsRemaining, currentLyrics = "", lyricVersions = {}, selectedVersion = 1, onDone }) => {
  const [note, setNote] = useState("");
  const [lyricsDraft, setLyricsDraft] = useState("");
  const [lyricsVersionSel, setLyricsVersionSel] = useState(selectedVersion);
  const [showLyricEditor, setShowLyricEditor] = useState(false);
  const [showDirectionEditor, setShowDirectionEditor] = useState(false);
  const [busy, setBusy] = useState(false);
  const freeLeft = (roundsRemaining ?? 0) > 0;
  const instrumental = track === "holdmusic";
  const scriptMode = track === "radio60"; // the radio spot's "lyrics" are its ad script
  const noun = scriptMode ? "script" : "lyrics";
  const versionKeys = Object.keys(lyricVersions).map(Number).sort((a, b) => a - b);
  const baseline = (lyricVersions[String(lyricsVersionSel)] || currentLyrics || "").trim();

  // Fresh state per track the dialog opens for
  useEffect(() => {
    if (open) {
      setNote("");
      setLyricsDraft("");
      setLyricsVersionSel(selectedVersion);
      setShowLyricEditor(false);
      setShowDirectionEditor(false);
    }
  }, [open, track, selectedVersion]);

  const editedLyrics = () => {
    if (!showLyricEditor) return "";
    const draft = lyricsDraft.trim();
    return draft && draft !== baseline ? draft : "";
  };

  const finish = () => {
    onOpenChange(false);
    if (onDone) onDone();
  };

  const hostedCheckout = async (text, lyrics) => {
    const { data } = await api.post("/payments/checkout", {
      lookup_key: "revision_single",
      origin_url: window.location.origin,
      site_key: siteKey,
      revision_track: track,
      revision_note: text,
      revision_lyrics: lyrics || undefined,
    });
    window.location.href = data.checkout_url;
  };

  const submit = async () => {
    const text = note.trim();
    const lyrics = editedLyrics();
    // No input required — "let the studio try again undirected" is a valid revision
    setBusy(true);
    try {
      if (freeLeft) {
        try {
          await api.post(`/sites/${siteKey}/revisions`, {
            track, note: text, custom_lyrics: lyrics || null,
          });
          toast.success("Revision is in the studio — we'll email you when the new cut lands.");
          finish();
          return;
        } catch (e) {
          const d = e.response?.data?.detail;
          if (e.response?.status === 409) {
            toast.info("A revision for this track is already being cut.");
            return;
          }
          if (!(e.response?.status === 402 && d?.needs_payment)) {
            toast.error(typeof d === "string" ? d : "Couldn't start the revision — try again.");
            return;
          }
          // Pool ran dry between page load and submit — fall through to the paid path
        }
      }
      try {
        const { data } = await api.post("/payments/one-click", {
          site_key: siteKey,
          offer: "revision_single",
          revision_track: track,
          revision_note: text,
          revision_lyrics: lyrics || undefined,
        });
        if (data.status !== "paid") throw new Error("requires_action");
        toast.success("Charged $12 to the card on file — revision is in the studio.");
        finish();
      } catch (e) {
        if (e.response?.status === 409 && e.response?.data?.detail !== "no_saved_card") {
          toast.info("A revision for this track is already being cut.");
          return;
        }
        await hostedCheckout(text, lyrics);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="revision-dialog" className="bg-ink border-white/15 text-slate-200 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Revise your {trackLabel}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Tell the studio what to change, or give the studio freedom to try again undirected —
            we'll cut 1 fresh full-quality version. The original stays put so you can compare side by side.
          </DialogDescription>
          {scriptMode && (
            <p data-testid="revision-donut-note" className="mt-2 text-xs text-emerald-400">
              The music-only radio bed gets a matching new version at the same time.
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {!instrumental && (
            <div>
              <button
                type="button"
                data-testid="revision-lyrics-toggle"
                onClick={() => {
                  const openNow = !showLyricEditor;
                  setShowLyricEditor(openNow);
                  if (openNow && !lyricsDraft.trim()) setLyricsDraft(baseline);
                }}
                className="flex items-center gap-2 text-sm font-semibold text-gold hover:text-neon transition-colors cursor-pointer"
              >
                <PenLine className="h-4 w-4" />
                {showLyricEditor ? `Keep the current ${noun}` : scriptMode ? "Want to edit the commercial script yourself?" : "Want to edit the lyrics yourself?"}
              </button>
              {showLyricEditor && (
                <div className="mt-3">
                  {versionKeys.length > 1 && (
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Start from:</span>
                      {versionKeys.map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`revision-lyrics-version-${n}`}
                          onClick={() => {
                            setLyricsVersionSel(n);
                            setLyricsDraft(lyricVersions[String(n)] || "");
                          }}
                          className={`text-xs rounded-full px-3 py-1.5 cursor-pointer transition-colors font-semibold ${
                            lyricsVersionSel === n
                              ? "bg-gold text-black"
                              : "border border-white/15 text-slate-300 hover:border-gold hover:text-gold"
                          }`}
                        >
                          Version {n} {noun}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    data-testid="revision-lyrics-input"
                    value={lyricsDraft}
                    onChange={(e) => setLyricsDraft(e.target.value)}
                    rows={Math.min(10, Math.max(4, lyricsDraft.split("\n").length + 1))}
                    className="w-full bg-obsidian/80 border border-gold/30 rounded-xl px-4 py-3.5 text-base font-display font-bold placeholder:text-slate-600 focus:outline-none focus:border-gold transition-colors"
                  />
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                    We'll sing YOUR exact words in the new full-quality version. Keep lines short
                    and singable — 4 to 9 syllables each works best, and end on the business name.
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <button
              type="button"
              data-testid="revision-direction-toggle"
              onClick={() => setShowDirectionEditor((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gold hover:text-neon transition-colors cursor-pointer"
            >
              <Music className="h-4 w-4" />
              {showDirectionEditor ? "No musical direction needed" : "Want to give more musical direction?"}
            </button>
            {showDirectionEditor && (
              <div className="mt-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      data-testid={`revision-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setNote((n) => (n ? `${n} ${c.toLowerCase()}` : c))}
                      className="text-xs rounded-full border border-white/15 px-3 py-1.5 text-slate-300 hover:border-gold hover:text-gold transition-colors cursor-pointer"
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="revision-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder='"make it slower", "more country", "female vocalist vibe"...'
                  className="w-full rounded-xl border border-white/15 bg-obsidian/70 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-gold resize-none"
                />
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                  Tell the studio what to change about the SOUND — the new version follows your direction.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p data-testid="revision-rounds-note" className="text-xs text-slate-500">
            {freeLeft
              ? `${roundsRemaining} free revision round${roundsRemaining === 1 ? "" : "s"} left in your package`
              : "Free rounds used up — extra revisions are $12 each"}
          </p>
          <button
            data-testid="revision-submit-btn"
            onClick={submit}
            disabled={busy}
            className="bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-6 py-3 rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
            {freeLeft ? "SEND TO THE STUDIO" : "REVISE — $12 (ONE CLICK)"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
